/**
 * The preset catalog as data: the eight named presets every learning surface starts from, and the
 * two named layer-1 picks (Pitch-Only, Rhythm-Only). Every value here is plain data - no function
 * anywhere - so the same entries can be stored or sent as JSON and read back with `loadPreset`.
 *
 * All presets share one note judgment by default: pitch gated, onset and offset on credit curves,
 * worst-of. Sight reading runs two presets in parallel over that one judgment (Flow for clearance,
 * the Complexity Verdict as the controller); they diverge at combining.
 *
 * The curve positions and the Complexity Verdict's lines are starting values: replaying recorded
 * sessions through the old and new grading is what settles them.
 */

import { CreditCurve } from './creditCurve';
import { ONSET_CURVES } from './curveLibrary';
import { JudgmentConfig } from './grader';
import { NoteJudgmentConfig } from './noteJudgment';
import { JudgmentPick, PresetDefinition } from './presetData';
import { GradingConfigError } from './types';

// ---------------------------------------------------------------------------------------------
// Shared curves and judgments.

/** Full credit for the written pitch only. */
const PITCH_SPIKE: CreditCurve = {
  unit: 'semitones',
  plateau: [0, 0],
  below: [{ distance: 0, credit: 0, shape: 'step' }],
  above: [{ distance: 0, credit: 0, shape: 'step' }],
};

/** Full credit within 0.2 beat of the written onset; 0.7 out to the binding window; then 0. */
const ONSET_CURVE: CreditCurve = ONSET_CURVES.standard;

/** Full credit within 0.3 beat of the written release; 0.7 out to 0.6 beat; then 0. */
const OFFSET_CURVE: CreditCurve = {
  unit: 'beats',
  plateau: [-0.3, 0.3],
  below: [
    { distance: 0, credit: 0.7, shape: 'step' },
    { distance: 0.3, credit: 0, shape: 'step' },
  ],
  above: [
    { distance: 0, credit: 0.7, shape: 'step' },
    { distance: 0.3, credit: 0, shape: 'step' },
  ],
};

/** Held length within a quarter of the written length; 0.7 out to half of it; then 0. */
const DURATION_CURVE: CreditCurve = {
  unit: 'proportion',
  plateau: [-0.25, 0.25],
  below: [
    { distance: 0, credit: 0.7, shape: 'step' },
    { distance: 0.25, credit: 0, shape: 'step' },
  ],
  above: [
    { distance: 0, credit: 0.7, shape: 'step' },
    { distance: 0.25, credit: 0, shape: 'step' },
  ],
};

/** The shared default: pitch gated, onset and offset on credit curves, worst-of. */
const SHARED_NOTE_JUDGMENT: NoteJudgmentConfig = {
  dimensions: {
    pitch: { curve: PITCH_SPIKE, gate: { passAt: 1 } },
    onset: { curve: ONSET_CURVE },
    offset: { curve: OFFSET_CURVE },
  },
  combiner: 'worst-of',
};

/** Sight reading and repertoire: an extra note is charged to the held note, capped at 0.7. */
const HELD_NOTE_JUDGMENT: JudgmentConfig = {
  matching: { rule: 'time-window', extraNotes: { counts: true, charge: 'capped', credit: 0.7 } },
  note: SHARED_NOTE_JUDGMENT,
};

/** Exercises: an extra note is a full red in its own right. */
const EXERCISE_JUDGMENT: JudgmentConfig = {
  matching: { rule: 'time-window', extraNotes: { counts: true, charge: 'own-error' } },
  note: SHARED_NOTE_JUDGMENT,
};

/** Timed tutorial steps: pitch and timing; extra notes do not count. */
const TUTORIAL_JUDGMENT: JudgmentConfig = {
  matching: { rule: 'time-window', extraNotes: { counts: false } },
  note: SHARED_NOTE_JUDGMENT,
};

// ---------------------------------------------------------------------------------------------
// The layer-1 picks.

export const PITCH_ONLY: JudgmentPick = {
  id: 'pitch-only',
  name: 'Pitch-Only',
  description:
    'Pitch and extra notes, timing ignored: order matching, the written pitch gates the note.',
  judgment: {
    matching: { rule: 'order', extraNotes: { counts: true, charge: 'capped', credit: 0.7 } },
    note: {
      dimensions: { pitch: { curve: PITCH_SPIKE, gate: { passAt: 1 } } },
      combiner: 'worst-of',
    },
  },
};

export const RHYTHM_ONLY: JudgmentPick = {
  id: 'rhythm-only',
  name: 'Rhythm-Only',
  description:
    'Start right and hold right, any key counts: onset and held length, for tap and clap rhythm exercises.',
  judgment: {
    matching: { rule: 'time-window', extraNotes: { counts: true, charge: 'own-error' } },
    note: {
      dimensions: { onset: { curve: ONSET_CURVE }, duration: { curve: DURATION_CURVE } },
      combiner: 'worst-of',
    },
  },
};

export const JUDGMENT_PICKS: readonly JudgmentPick[] = [PITCH_ONLY, RHYTHM_ONLY];

// ---------------------------------------------------------------------------------------------
// The presets.

const EVERY_NOTE_MUST_PASS: PresetDefinition['scoring'] = {
  combining: {
    pile: { unit: 'note' },
    levels: [
      {
        scope: { kind: 'bounded', boundary: { by: 'take' }, unreached: 'missed' },
        input: { kind: 'credit' },
        statistic: 'minimum',
      },
    ],
  },
  verdict: {
    kind: 'cut-lines',
    labels: ['not yet', 'pass'],
    tests: [{ view: 'combined', lines: [1] }],
  },
};

const ONE_PASS_CLEARS: PresetDefinition['policy'] = {
  clearance: {
    shape: { kind: 'count-up', success: ['pass'] },
    target: { lines: [1], labels: ['not cleared', 'cleared'] },
    resets: [],
  },
};

export const FLOW: PresetDefinition = {
  id: 'flow',
  name: 'Flow',
  description:
    'A phrase earns a chevron when the rolling 40-note recency-weighted accuracy stays at or above 95 throughout; it loses one when the phrase ends below 75; 4 chevrons clear.',
  role: 'clearance',
  surfaces: ['sight-reading'],
  consumers: [],
  material: 'continuous',
  liveAxes: ['tempo', 'complexity'],
  advancement: { onMistake: 'move-on', pace: 'set-tempo' },
  judgment: HELD_NOTE_JUDGMENT,
  scoring: {
    combining: {
      pile: { unit: 'note' },
      levels: [
        {
          scope: { kind: 'rolling', size: 40, per: 'items' },
          recency: { kind: 'rank' },
          input: { kind: 'credit' },
          statistic: 'mean',
        },
        {
          scope: {
            kind: 'bounded',
            boundary: { by: 'group', key: 'phrase' },
            unreached: 'excluded',
          },
          input: { kind: 'credit' },
          statistic: 'minimum',
        },
      ],
    },
    // Earn on the phrase's minimum running score; lose only on its final value, so a dip below
    // 75 that recovers by the end of the phrase holds.
    verdict: {
      kind: 'cut-lines',
      labels: ['lose', 'hold', 'earn'],
      tests: [
        { view: 'combined', lines: [0.95], labels: ['hold', 'earn'] },
        { view: 'combined', statistic: 'final', lines: [0.75], labels: ['lose', 'earn'] },
      ],
    },
  },
  policy: {
    chevrons: {
      shape: { kind: 'net-counter', earn: ['earn'], lose: ['lose'], floor: 0, ceiling: 4 },
      target: { lines: [4], labels: ['in progress', 'cleared'] },
      resets: [{ kind: 'session-boundary' }],
    },
  },
};

export const COMPLEXITY_VERDICT: PresetDefinition = {
  id: 'complexity-verdict',
  name: 'Complexity Verdict',
  description:
    'Per phrase, weighted errors (red 1, yellow 0.5) as a rate; green, yellow or red drives the climb and drop; banks nothing.',
  role: 'controller',
  surfaces: ['sight-reading'],
  consumers: [],
  material: 'continuous',
  liveAxes: ['tempo', 'complexity'],
  advancement: { onMistake: 'move-on', pace: 'set-tempo' },
  judgment: HELD_NOTE_JUDGMENT,
  scoring: {
    combining: {
      pile: { unit: 'note' },
      levels: [
        {
          scope: {
            kind: 'bounded',
            boundary: { by: 'group', key: 'phrase' },
            unreached: 'excluded',
          },
          input: { kind: 'band-weight', weights: { red: 1, yellow: 0.5 } },
          statistic: 'mean',
        },
      ],
    },
    // Starting lines: at most 1 / at most 3 weighted errors in an 8-note phrase, as a rate.
    verdict: {
      kind: 'cut-lines',
      labels: ['green', 'yellow', 'red'],
      tests: [{ view: 'combined', lines: [0.125, 0.375] }],
      atLine: 'lower',
      better: 'lower',
    },
  },
  policy: {},
};

export const RUNNING_ACCURACY: PresetDefinition = {
  id: 'running-accuracy',
  name: 'Running Accuracy',
  description:
    'Mean credit since the loop began, unreached notes excluded; shown, not judged (the surface may start the display at 100%).',
  role: 'feedback',
  surfaces: ['repertoire'],
  consumers: [],
  material: 'discrete',
  liveAxes: [],
  advancement: { onMistake: 'move-on', pace: 'set-tempo' },
  judgment: HELD_NOTE_JUDGMENT,
  scoring: {
    combining: {
      pile: { unit: 'note' },
      levels: [
        {
          scope: { kind: 'bounded', boundary: { by: 'since-reset' }, unreached: 'excluded' },
          input: { kind: 'credit' },
          statistic: 'mean',
        },
      ],
    },
  },
  policy: {},
};

export const PERFECT_LOOP: PresetDefinition = {
  id: 'perfect-loop',
  name: 'Perfect Loop',
  description:
    'A loop pass is perfect only with no yellow or red; a streak of perfect passes, reset by a loop-area or tempo change or a long pause.',
  role: 'feedback',
  surfaces: ['repertoire'],
  consumers: [],
  material: 'discrete',
  liveAxes: [],
  advancement: { onMistake: 'move-on', pace: 'set-tempo' },
  judgment: HELD_NOTE_JUDGMENT,
  scoring: {
    combining: {
      pile: { unit: 'note' },
      levels: [
        {
          scope: { kind: 'bounded', boundary: { by: 'take' }, unreached: 'missed' },
          input: { kind: 'credit' },
          statistic: 'minimum',
        },
      ],
    },
    verdict: {
      kind: 'cut-lines',
      labels: ['imperfect', 'perfect'],
      tests: [{ view: 'combined', lines: [1] }],
    },
  },
  policy: {
    perfectStreak: {
      shape: { kind: 'streak', success: ['perfect'] },
      resets: [{ kind: 'context-change' }, { kind: 'time-decay', idleMs: 60000 }],
    },
  },
};

export const PIECE_COMPLETION: PresetDefinition = {
  id: 'piece-completion',
  name: 'Piece Completion',
  description:
    'The whole piece in one playthrough, unreached notes counted as missed, mean credit; complete at 100%; keeps the personal best.',
  role: 'clearance',
  surfaces: ['repertoire'],
  consumers: [],
  material: 'discrete',
  liveAxes: [],
  advancement: { onMistake: 'move-on', pace: 'set-tempo' },
  judgment: HELD_NOTE_JUDGMENT,
  scoring: {
    combining: {
      pile: { unit: 'note' },
      levels: [
        {
          scope: { kind: 'bounded', boundary: { by: 'take' }, unreached: 'missed' },
          input: { kind: 'credit' },
          statistic: 'mean',
        },
      ],
    },
    verdict: {
      kind: 'cut-lines',
      labels: ['in progress', 'complete'],
      tests: [{ view: 'combined', lines: [1] }],
    },
  },
  policy: {
    personalBest: {
      shape: { kind: 'personal-best' },
      target: { lines: [1], labels: ['in progress', 'complete'] },
      resets: [],
    },
  },
};

export const TAKE_LADDER: PresetDefinition = {
  id: 'take-ladder',
  name: 'Take Ladder',
  description:
    'A tally ladder per take: all green is flawless, any red fails, more green than yellow is clean, else failed; 3 clean takes clear, 3 flawless in a row master.',
  role: 'clearance',
  surfaces: ['exercise'],
  consumers: [],
  material: 'discrete',
  liveAxes: [],
  advancement: { onMistake: 'move-on', pace: 'set-tempo' },
  judgment: EXERCISE_JUDGMENT,
  scoring: {
    combining: {
      pile: { unit: 'note' },
      levels: [
        {
          scope: { kind: 'bounded', boundary: { by: 'take' }, unreached: 'missed' },
          input: { kind: 'tally' },
          statistic: 'sum',
        },
      ],
    },
    verdict: {
      kind: 'tally-ladder',
      view: 'combined',
      rungs: [
        {
          label: 'flawless',
          when: {
            all: [
              { compare: [{ band: 'yellow' }, '==', 0] },
              { compare: [{ band: 'red' }, '==', 0] },
            ],
          },
        },
        { label: 'failed', when: { compare: [{ band: 'red' }, '>=', 1] } },
        { label: 'clean', when: { compare: [{ band: 'green' }, '>', { band: 'yellow' }] } },
      ],
      otherwise: 'failed',
    },
  },
  policy: {
    clearance: {
      shape: { kind: 'count-up', success: ['clean', 'flawless'] },
      target: { lines: [3], labels: ['not cleared', 'cleared'] },
      resets: [],
    },
    mastery: {
      shape: { kind: 'streak', success: ['flawless'] },
      target: { lines: [3], labels: ['not mastered', 'mastered'] },
      resets: [],
    },
  },
};

export const TIMED_OBJECTIVE: PresetDefinition = {
  id: 'timed-objective',
  name: 'Timed Objective',
  description: 'Time-window matching, pitch and timing, every note must pass; one pass clears.',
  role: 'clearance',
  surfaces: ['tutorial'],
  consumers: [],
  material: 'discrete',
  liveAxes: [],
  advancement: { onMistake: 'move-on', pace: 'set-tempo' },
  judgment: TUTORIAL_JUDGMENT,
  scoring: EVERY_NOTE_MUST_PASS,
  policy: ONE_PASS_CLEARS,
};

export const UNTIMED_OBJECTIVE: PresetDefinition = {
  id: 'untimed-objective',
  name: 'Untimed Objective',
  description:
    'Wait for right (timing off, Hold), order matching, pitch only, every note in sequence; one pass clears.',
  role: 'clearance',
  surfaces: ['tutorial'],
  consumers: [],
  material: 'discrete',
  liveAxes: [],
  advancement: { onMistake: 'hold' },
  judgment: PITCH_ONLY.judgment,
  // Under Hold every note reached is right, so with unreached notes counted as missed "every
  // note must pass" reads completion, not final accuracy.
  scoring: EVERY_NOTE_MUST_PASS,
  policy: ONE_PASS_CLEARS,
};

export const PRESET_CATALOG: readonly PresetDefinition[] = [
  FLOW,
  COMPLEXITY_VERDICT,
  RUNNING_ACCURACY,
  PERFECT_LOOP,
  PIECE_COMPLETION,
  TAKE_LADDER,
  TIMED_OBJECTIVE,
  UNTIMED_OBJECTIVE,
];

/** A catalog preset by id. Throws {@link GradingConfigError} for an unknown id. */
export function catalogPreset(id: string): PresetDefinition {
  const found = PRESET_CATALOG.filter((preset) => preset.id === id);
  if (found.length === 0) {
    throw new GradingConfigError(`no catalog preset "${id}"`);
  }
  return found[0];
}

/** A judgment pick by id. Throws {@link GradingConfigError} for an unknown id. */
export function judgmentPick(id: string): JudgmentPick {
  const found = JUDGMENT_PICKS.filter((pick) => pick.id === id);
  if (found.length === 0) {
    throw new GradingConfigError(`no judgment pick "${id}"`);
  }
  return found[0];
}
